import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Box, Grid, Card, Button, Avatar } from '@mui/material';
import { LocalFlorist, Phone } from '@mui/icons-material';
import Layout from '../components/Layout';

const glassStyle = {
  background: 'rgba(255, 255, 255, 0.25)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)'
};

const GardenDesign = () => {
  return (
    <Layout>
      <Helmet>
        <title>Garden Design Vancouver BC | Backyard Landscaping North Vancouver | Green Mountain</title>
        <meta name="description" content="Professional garden design Vancouver BC. Custom backyard landscaping North Vancouver & West Vancouver. Plant selection, garden planning. Free quotes (604) 202-0053" />
        <meta name="keywords" content="garden design vancouver, backyard landscaping north vancouver, garden planning west vancouver, plant selection bc, landscape design vancouver, outdoor garden design" />
        <link rel="canonical" href="https://greenmountainlandscaping.ca/garden-design" />
      </Helmet>
      
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("/images/o.jpg")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: -2
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(27, 94, 32, 0.8) 0%, rgba(46, 125, 50, 0.7) 100%)',
            zIndex: -1
          }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ ...glassStyle, p: { xs: 3, md: 6 }, color: 'white', textAlign: 'center' }}>
            <Avatar sx={{ bgcolor: 'white', color: 'primary.main', width: 80, height: 80, mx: 'auto', mb: 3 }}>
              <LocalFlorist sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h1" component="h1" sx={{ mb: 3, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
              Professional Garden Design Vancouver BC
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
              Transform your outdoor space with custom garden design
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" size="large" href="tel:+16042020053" startIcon={<Phone />} sx={{ bgcolor: 'white', color: 'primary.main' }}>
                (604) 202-0053
              </Button>
              <Button variant="outlined" size="large" href="/#contact" sx={{ borderColor: 'white', color: 'white' }}>
                Get Free Design Quote
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
      
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h1" component="h1" sx={{ mb: 4, color: 'primary.main' }}>
          Professional Garden Design Vancouver BC
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
          Transform your outdoor space with expert <strong>garden design in Vancouver</strong>. Green Mountain Landscaping creates stunning backyard landscaping in North Vancouver, West Vancouver, and Metro Vancouver areas with custom plant selection and garden planning.
        </Typography>

        <Typography variant="h2" component="h2" sx={{ mb: 3, color: 'primary.main' }}>
          Custom Backyard Landscaping North Vancouver
        </Typography>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {[
            'Custom Garden Planning Vancouver',
            'Native Plant Selection BC',
            'Seasonal Garden Design',
            'Perennial Garden Installation',
            'Flower Bed Design Vancouver',
            'Garden Lighting Design'
          ].map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ p: 3, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocalFlorist sx={{ color: 'primary.main', mr: 1 }} />
                  <Typography variant="h6">{service}</Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h3" component="h3" sx={{ mb: 3, color: 'primary.main' }}>
          How to Design a Small Garden in BC Climate
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 4 }}>
          Our <strong>garden design Vancouver</strong> experts understand the Pacific Northwest climate. We create beautiful, sustainable gardens that thrive in British Columbia's unique weather conditions, from West Vancouver to North Vancouver and beyond.
        </Typography>

        <Button variant="contained" size="large" href="tel:+16042020053" sx={{ mr: 2 }}>
          Call (604) 202-0053
        </Button>
        <Button variant="outlined" size="large" href="/#contact">
          Get Free Design Quote
        </Button>
      </Container>
    </Layout>
  );
};

export default GardenDesign;